<script setup lang="ts">
import { computed, ref, toRefs, watchEffect } from 'vue';
import { useDisplay } from 'vuetify';
import { Details, ControlButtons, AdditionalControl } from '../types/share';
import BottomMenu from '../BottomMenu.vue';
import ShareDetailCard from './ShareDetailCard.vue';
import CollectionDescription from '../collection/CollectionDescription.vue';
import CollectionName from '../collection/CollectionName.vue';
import ShareCtrlBtn from './ShareCtrlBtn.vue';
import AdditionalCtrlMenu from './AdditionalCtrlMenu.vue';
import AdditionalCtrlBtn from './AdditionalCtrlBtn.vue';

type Emits = {
  (e: string, ...args: any[]): void; // dynamic events for control buttons
  (e: 'onEditName', name: string): void;
  (e: 'onEditDescription', name: string): void;
};
const emit = defineEmits<Emits>();

type Props = {
  name: string;
  details: Details[];
  controlButtons?: ControlButtons[];
  iconSizes?: Record<string, string>;
  shareCtrlIconSize?: string;
  detailIconSize?: number | string;
  hideEntityIcon?: boolean;
  hideDescription?: boolean;
  canEditIcons?: boolean;
  canEditName?: boolean;
  description?: string;
  additionalControls?: AdditionalControl[];
  rounded?: boolean;
  bookTitleSize?: 'sm' | 'md' | 'lg';
  iconBg?: string;
};
const props = withDefaults(defineProps<Props>(), {
  name: '',
  details: () => [],
  controlButtons: () => [],
  iconSizes: () => ({
    xs: '80px',
    sm: '100px',
    md: '120px',
    lg: '134px',
    xl: '150px',
    xxl: '150px',
    null: '150px',
  }),
  hideEntityIcon: false,
  hideDescription: false,
  canEditIcons: false,
  canEditName: false,
  description: undefined,
  additionalControls: () => [],
  rounded: true,
  bookTitleSize: 'md',
  shareCtrlIconSize: 'large',
  detailIconSize: 24,
});
const { canEditName, hideDescription, iconSizes } = toRefs(props);

const { name: breakpointName, smAndDown, xs, platform } = useDisplay();

const editedName = ref<string>(props.name);
const editedDescription = ref<string>(props.description ?? '');
const sheet = ref<boolean>(false);
const timer = ref<any | null>(null);

const isTouchDevice = computed(() => platform.value.touch);
const avatarSize = computed(() => iconSizes?.value[breakpointName.value]);
const canEditDescription = computed(() => (canEditName.value || editedDescription.value) && !hideDescription.value);

const doWithDelay = (callback: () => void) => {
  if (timer.value) {
    clearTimeout(timer.value);
    timer.value = null;
  }
  timer.value = setTimeout(() => {
    callback();
  }, 1300);
};

const confirmEditingDescription = () => {
  doWithDelay(() => {
    emit('onEditDescription', editedDescription.value);
  });
};

const confirmEditing = () => {
  doWithDelay(() => {
    emit('onEditName', editedName.value);
  });
};

const onAdditionalControlEvent = (event: string) => emit(event);

watchEffect(() => {
  editedName.value = props.name;
  editedDescription.value = props.description ?? '';
});
</script>

<template>
  <div>
    <div class="text-center">
      <!-- Entity image -->
      <v-avatar
        v-if="!hideEntityIcon"
        class="rounded share-icon"
        :rounded="0"
        :size="avatarSize"
        :color="iconBg || 'primary'"
      >
        <slot name="icon"></slot>
        <div class="share-icon-settings">
          <slot name="icon-top"></slot>
        </div>
      </v-avatar>
      <!-- END image -->
      <div
        class="d-flex align-center mt-1"
        :class="$slots['controls-leading'] ? 'justify-space-between' : 'justify-center'"
      >
        <slot name="controls-leading"></slot>
        <div class="d-flex align-center">
          <slot name="primary-controls">
            <template v-for="ctrlBtn in controlButtons" :key="ctrlBtn.key || ctrlBtn.title">
              <ShareCtrlBtn
                v-if="!ctrlBtn.isActive"
                :ctrlBtn="ctrlBtn"
                :is-touch-device="isTouchDevice"
                :icon-size="shareCtrlIconSize"
                @click="() => emit(ctrlBtn.eventName)"
              >
                <slot :name="`control-button-${ctrlBtn.key ?? ctrlBtn.title}`"></slot>
              </ShareCtrlBtn>
            </template>
          </slot>

          <template v-if="additionalControls.length > 0">
            <slot name="overflow-controls">
              <BottomMenu
                v-if="smAndDown"
                v-model="sheet"
                :items="additionalControls"
                @close="sheet = false"
                @item-event="onAdditionalControlEvent"
              >
              </BottomMenu>
              <AdditionalCtrlBtn :is-touch-device="isTouchDevice" @click="() => (sheet = !sheet)">
                <AdditionalCtrlMenu
                  v-if="!smAndDown"
                  :items="additionalControls"
                  @item-event="onAdditionalControlEvent"
                />
              </AdditionalCtrlBtn>
            </slot>
          </template>
        </div>
      </div>
      <h1 class="py-2">
        <slot name="prepend-label"></slot>
        <slot name="label">
          <CollectionName
            :can-edit-name="canEditName"
            v-model="editedName"
            :title-size="bookTitleSize"
            @confirmed="confirmEditing"
          />
        </slot>
      </h1>
    </div>
    <div v-if="canEditDescription" class="mt-4 mb-4">
      <slot name="description">
        <CollectionDescription
          v-model="editedDescription"
          :can-edit-description="canEditName"
          @confirmed="confirmEditingDescription"
        />
      </slot>
    </div>
    <v-spacer v-else class="mt-2 mb-4" />
    <div
      class="share-details d-flex ml-n4 ml-md-0 mr-n4' mr-md-0' flex-md-wrap"
      :class="{ 'pl-4': smAndDown, 'flex-wrap': smAndDown && !xs, 'details-scroller flex-nowrap': xs }"
    >
      <slot name="details" :details="details">
        <template v-for="item in details">
          <ShareDetailCard
            v-if="item.value"
            :item="item"
            :rounded="rounded"
            :detail-icon-size="detailIconSize"
            :key="item.title"
          />
        </template>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.details-scroller {
  overflow-x: auto;
  scrollbar-width: none;

  > * {
    min-width: fit-content;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.share-icon {
  position: relative;
  overflow: visible;

  &-settings {
    position: absolute;
    top: -10px;
    left: calc(0% - 14px);
    cursor: pointer;
  }
}

.share-details {
  overflow-x: auto;
  gap: 8px;
}
</style>
